import { useEffect, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { AppWindow, MoreVertical, Plus, Search } from 'lucide-react';
import type { AppDto, CreateAppInput, DeviceDto, UpdateAppInput } from '@gc/shared';

import { api } from '@/lib/api';
import { DevicePathEditor, toDevicePathPayload, toOverrideMap } from './DevicePathEditor';
import { Field, ImageUpload, ghostPillClass, inputClass, pillClass } from './controls';
import type { MediaIdEdit } from './gameLogic';
import {
  launchTargetFieldProblems,
  mapDevicePathFieldErrors,
  overrideErrorMap,
  overrideField,
} from './launchTargetPresenter';
import { toFieldErrors, useResource, type FieldError } from './useSettings';

/**
 * Apps (SPEC §6.7) — what customers may open from the portal's Apps tab.
 * Same default-path + per-device-override model as games.
 */
export function AppsPage() {
  const { t } = useTranslation();

  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [editing, setEditing] = useState<AppDto | null>(null);
  const [creating, setCreating] = useState(false);
  const [menuFor, setMenuFor] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(search.trim()), 300);
    return () => clearTimeout(timer);
  }, [search]);

  const query = debounced ? `?search=${encodeURIComponent(debounced)}` : '';
  const { data, loading, reload } = useResource<AppDto[]>(`/settings/apps${query}`);
  const apps = data ?? [];

  async function handleDelete(app: AppDto): Promise<void> {
    if (!window.confirm(t('settings.apps.confirmDelete', { name: app.name }))) return;
    setMenuFor(null);
    try {
      await api.delete(`/settings/apps/${app.id}`);
      await reload();
    } catch (caught) {
      setError(toFieldErrors(caught, t('settings.apps.deleteFailed'))[0]?.message ?? null);
    }
  }

  return (
    <div className="p-6">
      <header className="mb-4 flex flex-wrap items-center gap-3">
        <h1 className="text-xl font-semibold text-content">{t('settings.nav.apps')}</h1>

        <div className="relative">
          <Search
            className="pointer-events-none absolute top-1/2 h-4 w-4 -translate-y-1/2 text-content-muted"
            style={{ insetInlineStart: '0.6rem' }}
            aria-hidden
          />
          <input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={t('settings.apps.searchPlaceholder')}
            className={`${inputClass} w-56 ps-8`}
          />
        </div>

        <div className="flex-1" />

        <button type="button" onClick={() => setCreating(true)} className={pillClass}>
          <span className="inline-flex items-center gap-1">
            <Plus className="h-4 w-4" aria-hidden />
            {t('settings.apps.addNew')}
          </span>
        </button>
      </header>

      {error ? (
        <p role="alert" className="mb-3 text-sm text-danger">
          {error}
        </p>
      ) : null}

      {loading && apps.length === 0 ? (
        <p className="text-sm text-content-muted">{t('common.loading')}</p>
      ) : apps.length === 0 ? (
        <p className="text-sm text-content-muted">{t('common.noResults')}</p>
      ) : (
        <div className="grid gap-4 [grid-template-columns:repeat(auto-fill,minmax(8rem,1fr))]">
          {apps.map((app) => (
            <article
              key={app.id}
              className="relative overflow-hidden rounded-card border border-subtle bg-raised"
            >
              <div className="grid aspect-square place-items-center bg-input">
                {app.iconUrl ? (
                  <img src={app.iconUrl} alt="" className="h-16 w-16 object-contain" />
                ) : (
                  <AppWindow className="h-8 w-8 text-content-muted" aria-hidden />
                )}
              </div>

              <div className="flex items-start gap-1 p-2">
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm text-content">{app.name}</p>
                  <p className="truncate text-xs text-content-muted">
                    {app.devicePaths.length > 0
                      ? t('settings.paths.overrideCount', { count: app.devicePaths.length })
                      : t('settings.paths.defaultOnly')}
                  </p>
                </div>

                <button
                  type="button"
                  onClick={() => setMenuFor(menuFor === app.id ? null : app.id)}
                  title={t('common.actions')}
                  aria-label={t('common.actions')}
                  className="grid h-7 w-7 shrink-0 place-items-center rounded-full text-content-secondary hover:bg-hover"
                >
                  <MoreVertical className="h-4 w-4" aria-hidden />
                </button>
              </div>

              {menuFor === app.id ? (
                <div className="absolute end-2 bottom-10 z-10 w-32 overflow-hidden rounded-control border border-subtle bg-overlay shadow-xl">
                  <button
                    type="button"
                    onClick={() => {
                      setEditing(app);
                      setMenuFor(null);
                    }}
                    className="block w-full px-3 py-2 text-start text-sm text-content hover:bg-hover"
                  >
                    {t('common.edit')}
                  </button>
                  <button
                    type="button"
                    onClick={() => void handleDelete(app)}
                    className="block w-full px-3 py-2 text-start text-sm text-danger hover:bg-hover"
                  >
                    {t('common.delete')}
                  </button>
                </div>
              ) : null}
            </article>
          ))}
        </div>
      )}

      {creating ? (
        <AppDrawer app={null} onClose={() => setCreating(false)} onSaved={() => void reload()} />
      ) : null}

      {editing ? (
        <AppDrawer app={editing} onClose={() => setEditing(null)} onSaved={() => void reload()} />
      ) : null}
    </div>
  );
}

function AppDrawer({
  app,
  onClose,
  onSaved,
}: {
  app: AppDto | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useTranslation();
  const isEdit = app !== null;

  const devices = useResource<{ devices: DeviceDto[] }>('/devices');
  // Until the list has loaded there is no way to tell a hidden device from
  // a slow one, so no override is treated as unlisted before then.
  const visibleDeviceIds = devices.data ? devices.data.devices.map((entry) => entry.id) : undefined;

  const [name, setName] = useState(app?.name ?? '');
  const [defaultPath, setDefaultPath] = useState(app?.executablePath ?? '');
  const [overrides, setOverrides] = useState<Record<string, string>>(
    toOverrideMap(app?.devicePaths ?? []),
  );
  // `undefined` = untouched, `null` = the trash icon, a string = a new upload.
  // See `MediaIdEdit`: two states could not say "remove it", so the icon
  // vanished from the preview and came back on reload.
  const [iconMediaAssetId, setIconMediaAssetId] = useState<MediaIdEdit>(undefined);
  const [iconUrl, setIconUrl] = useState<string | null>(app?.iconUrl ?? null);

  const [errors, setErrors] = useState<FieldError[]>([]);
  const [submitting, setSubmitting] = useState(false);

  const errorFor = (field: string) => errors.find((issue) => issue.field === field)?.message;
  const clearError = (field: string) =>
    setErrors((current) => current.filter((issue) => issue.field !== field));

  function handleDefaultPathChange(value: string): void {
    setDefaultPath(value);
    clearError('executablePath');
  }

  function handleOverridesChange(next: Record<string, string>): void {
    for (const deviceId of Object.keys(next)) {
      if (next[deviceId] !== overrides[deviceId]) clearError(overrideField(deviceId));
    }
    setOverrides(next);
  }

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    setErrors([]);

    // The same parser the API runs, run first: a path or link no PC could
    // open is refused under its own field without a round trip.
    const problems = launchTargetFieldProblems(defaultPath, overrides, visibleDeviceIds);
    if (problems.length > 0) {
      setErrors(problems.map(({ field, key, params }) => ({ field, message: t(key, params) })));
      return;
    }

    setSubmitting(true);

    const common = {
      name,
      executablePath: defaultPath.trim() || null,
      devicePaths: toDevicePathPayload(overrides),
      // `!== undefined`, never truthiness: `null` is what clears the column.
      ...(iconMediaAssetId !== undefined ? { iconMediaAssetId } : {}),
    };

    try {
      if (isEdit && app) {
        await api.patch(`/settings/apps/${app.id}`, common satisfies UpdateAppInput);
      } else {
        await api.post('/settings/apps', common satisfies CreateAppInput);
      }
      onSaved();
      onClose();
    } catch (caught) {
      // `devicePaths.<index>.path` names an entry of the array sent above;
      // file it under that entry's device so it shows beside the row.
      setErrors(
        mapDevicePathFieldErrors(
          toFieldErrors(caught, t('settings.apps.saveFailed')),
          overrides,
          visibleDeviceIds,
        ),
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/60">
      <form
        onSubmit={(event) => void handleSubmit(event)}
        className="flex h-full w-full max-w-xl flex-col border-s border-subtle bg-raised shadow-2xl"
      >
        <header className="border-b border-subtle p-5">
          <h2 className="text-lg font-semibold text-content">
            {isEdit ? t('settings.apps.edit') : t('settings.apps.addNew')}
          </h2>
        </header>

        <div className="min-h-0 flex-1 space-y-3 overflow-auto p-5">
          <ImageUpload
            label={t('settings.apps.icon')}
            kind="APP_ICON"
            currentUrl={iconUrl}
            onUploaded={(assetId, url) => {
              setIconMediaAssetId(assetId);
              setIconUrl(url);
            }}
            onCleared={() => {
              setIconMediaAssetId(null);
              setIconUrl(null);
            }}
          />

          <Field label={t('common.name')} error={errorFor('name')}>
            <input
              value={name}
              onChange={(event) => setName(event.target.value)}
              required
              className={inputClass}
            />
          </Field>

          <DevicePathEditor
            devices={devices.data?.devices ?? []}
            defaultPath={defaultPath}
            onDefaultPathChange={handleDefaultPathChange}
            overrides={overrides}
            onOverridesChange={handleOverridesChange}
            detected={app?.devicePaths}
            defaultPathError={errorFor('executablePath')}
            overrideErrors={overrideErrorMap(errors)}
          />
        </div>

        <footer className="border-t border-subtle p-5">
          {errorFor('(root)') ? (
            <p role="alert" className="mb-3 text-sm text-danger">
              {errorFor('(root)')}
            </p>
          ) : null}
          <div className="flex justify-end gap-2">
            <button type="button" onClick={onClose} className={ghostPillClass}>
              {t('common.cancel')}
            </button>
            <button type="submit" disabled={submitting} className={pillClass}>
              {t('common.save')}
            </button>
          </div>
        </footer>
      </form>
    </div>
  );
}
